import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { mapToVerificationConfiguration } from '@percent/admin-dashboard/app/validationSubmissions/utils/configurationMappers'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { OrganisationRelationships } from '@percent/admin-dashboard/common/components/organisationRelationship/OrganisationRelationships'
import {
  useActivityTagsState,
  usePermissions,
  useQuery,
  useQueryList,
  useRequiredParams,
  useValidationSubmissionsAccess
} from '@percent/admin-dashboard/common/hooks'
import { getCountryFromAlpha3 } from '@percent/admin-dashboard/common/utility/getCountryFromCountryCode'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import {
  Organisation,
  ValidationRequestRejectionReasonCodes,
  VerificationAgentDetailsCard,
  VerificationDocumentsCard,
  VerificationMetadataCard,
  VerificationOrganisationDetailsCard,
  VerificationPartnerFieldsCard,
  VerificationSummaryCard
} from '@percent/domain/verifications'
import { Button, Tab } from '@percent/lemonade'
import styles from './ValidationSubmissionDetailsContainer.module.scss'
import { ValidationSubmissionTimeline } from './validationSubmissionTimeline/ValidationSubmissionTimeline'
import { ValidationSubmissionTopBar } from './validationSubmissionTopBar/ValidationSubmissionTopBar'
import { ValidationSubmissionRedemption } from './validationSubmissionRedemption/ValidationSubmissionRedemption'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'

export function ValidationSubmissionDetailsContainer() {
  const [activeTab, setActiveTab] = useState('')

  const { validationSubmissionsService, adminService } = useServices()
  const { t } = useTranslation()
  const { id } = useRequiredParams()
  const navigate = useNavigate()
  const { activityTags, isLoading: isActivityTagsLoading } = useActivityTagsState()
  const vsRedemptionFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_VS_REDEMPTION)
  const internalFgaOrgvRedemptionsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_ORGV_REDEMPTIONS
  )
  const { userHasPermissions } = usePermissions()
  const { canReadDocuments } = useValidationSubmissionsAccess()

  const canViewRedemption = internalFgaOrgvRedemptionsFeatureFlag
    ? userHasPermissions('redemptions:can_read')
    : vsRedemptionFeatureFlag

  const [
    {
      data: validationSubmission,
      isLoading: isValidationSubmissionLoading,
      errorMessage: validationSubmissionErrorMessage
    }
  ] = useQuery(validationSubmissionsService.getValidationSubmission, { id })

  const [
    {
      data: validationSubmissionConfiguration,
      isLoading: isValidationSubmissionConfigurationLoading,
      errorMessage: validationSubmissionConfigurationErrorMessage
    }
  ] = useQuery(
    validationSubmissionsService.getValidationSubmissionConfiguration,
    { id: validationSubmission?.configuration?.id },
    { skip: !validationSubmission?.configuration?.id }
  )

  const organisationId = validationSubmission?.organisation?.id

  // Fired from an effect rather than on mount because canReadDocuments starts false until
  // PostHog has resolved the rollout flag, and useQueryList latches its mount argument on
  // first render — passing it directly would skip the request for good.
  const [
    { dataOrNull: validationSubmissionDocuments, isLoading: isLoadingValidationSubmissionDocuments },
    { query: queryValidationSubmissionDocuments }
  ] = useQueryList(adminService.getValidationSubmissionDocuments, { validationSubmissionId: id }, false)

  useEffect(() => {
    if (canReadDocuments) {
      queryValidationSubmissionDocuments({ validationSubmissionId: id })
    }
  }, [canReadDocuments, id, queryValidationSubmissionDocuments])

  const [{ data: organisation, isLoading: isOrganisationLoading, errorMessage: organisationErrorMessage }] = useQuery(
    adminService.getOrganisation,
    { id: organisationId || '' },
    { skip: !organisationId }
  )
  const goToOrganisationPage = () => navigate(`/organizations/${organisationId}`)

  // The documents are one card on the page and carry their own loading state, so they no
  // longer hold up the whole page — otherwise the deferred fetch above would drop the
  // rendered page back to a spinner.
  if (isValidationSubmissionLoading || isOrganisationLoading) return <Loader />

  if (validationSubmissionErrorMessage || validationSubmissionConfigurationErrorMessage || organisationErrorMessage) {
    return (
      <ErrorView
        errorMessage={
          validationSubmissionErrorMessage || validationSubmissionConfigurationErrorMessage || organisationErrorMessage
        }
      />
    )
  }

  const hasNoEligibleOrganisationType =
    !!organisation &&
    validationSubmission?.validationRequest?.rejectionReasonCode ===
      ValidationRequestRejectionReasonCodes.NotEligible &&
    // TODO: double check after DELPHI PR created https://wearepercent.atlassian.net/browse/DELPHI-1062
    // !validationSubmission.configuration?.allowedOrganisations?.types?.some(item => organisation?.types?.includes(item))
    !validationSubmission.configuration?.allowedOrganisations?.types?.some(
      item => organisation?.types?.[item] === 'yes'
    )
  const hasMetadata = validationSubmission?.metadata && !!Object.keys(validationSubmission.metadata).length

  const checksLinks = {
    agentVerification: {
      jiraLink: validationSubmission?.agentVerification?.jiraLink,
      goToCheckLink: `/agent-verification/${validationSubmission?.agentVerification?.id}`
    },
    eligibility: {
      jiraLink: validationSubmission?.eligibility?.jiraLink,
      goToCheckLink: `/eligibility/${validationSubmission?.eligibility?.id}`
    },
    monitoring: {
      jiraLink: validationSubmission?.monitoring?.jiraLink,
      goToCheckLink: `/organizations/${organisation?.id}/compliance`
    },
    organisation: {
      jiraLink: validationSubmission?.validationRequest?.jiraLink,
      goToCheckLink: !validationSubmission?.validationRequest?.id
        ? `/organizations/${organisation?.id}`
        : `/validations/${validationSubmission?.validationRequest?.id}`
    }
  }

  return (
    <div>
      <ValidationSubmissionTopBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        id={validationSubmission.id}
        organizationName={validationSubmission?.organisation?.name ?? validationSubmission?.validationRequest?.name}
      />
      <Tab index={`/validation-submissions/${id}`} value={activeTab}>
        <div className={styles.applicationDetailsContainer}>
          <VerificationSummaryCard
            verification={validationSubmission}
            organisation={organisation as Organisation}
            hasNoEligibleOrganisationType={hasNoEligibleOrganisationType}
            cardTitle={t('typography.checksSummary')}
            checksLinks={checksLinks}
            configuration={
              validationSubmissionConfiguration && mapToVerificationConfiguration(validationSubmissionConfiguration)
            }
            activityTags={activityTags}
            isLoadingConfiguration={isValidationSubmissionConfigurationLoading || isActivityTagsLoading}
          />
          {validationSubmission?.agentVerification?.id && (
            <VerificationAgentDetailsCard
              agentName={`${validationSubmission?.agentVerification?.firstName} ${validationSubmission?.agentVerification?.lastName}`}
              agentEmail={validationSubmission?.agentVerification?.email}
              language={validationSubmission?.agentVerification?.language}
            />
          )}
          {!validationSubmission?.agentVerification?.id && validationSubmission.firstName && (
            <VerificationAgentDetailsCard
              agentName={`${validationSubmission.firstName} ${validationSubmission.lastName}`}
              agentEmail={validationSubmission.email}
            />
          )}
          {organisation && (
            <VerificationOrganisationDetailsCard
              organisation={organisation as Organisation}
              isLoading={isValidationSubmissionLoading}
              validationRequest={validationSubmission.validationRequest}
              eligibility={validationSubmission?.eligibility}
              hasNoEligibleOrganisationType={hasNoEligibleOrganisationType}
              getCountryNameByCode={getCountryFromAlpha3}
              action={
                organisationId && (
                  <Button variant="secondary" onClick={goToOrganisationPage}>
                    {t('typography.viewOrganizationDetails')}
                  </Button>
                )
              }
            />
          )}
          <OrganisationRelationships relationships={validationSubmission.organisation?.relationships} />
          {canViewRedemption && (
            <ValidationSubmissionRedemption
              code={validationSubmission.redemption?.code}
              configurationName={validationSubmission.configuration?.name}
              language={validationSubmission.agentVerification?.language}
            />
          )}
          {validationSubmission.partnerFields && !!Object.keys(validationSubmission.partnerFields)?.length && (
            <VerificationPartnerFieldsCard partnerFields={validationSubmission.partnerFields} />
          )}
          {hasMetadata && <VerificationMetadataCard metadata={validationSubmission.metadata!} />}
          {canReadDocuments && (
            <VerificationDocumentsCard
              documents={validationSubmissionDocuments || []}
              isLoading={isLoadingValidationSubmissionDocuments}
            />
          )}
        </div>
      </Tab>
      <Tab index={`/validation-submissions/${id}/timeline`} value={activeTab}>
        <ValidationSubmissionTimeline validationSubmission={validationSubmission} />
      </Tab>
    </div>
  )
}
